import { BellOff, Receipt, Users } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { toApiError } from '@/api/errors'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { SearchInput } from '@/components/ui/SearchInput'
import { Spinner } from '@/components/ui/Spinner'
import { Tabs } from '@/components/ui/Tabs'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { chatService, type Chat, type ChatKind } from '@/services/core/chat.service'
import { useStores } from '@/stores'
import { chatTitle, formatListTime, previewText } from './model'

const PAGE = 30
const POLL_MS = 15_000

export const CHAT_KINDS: ChatKind[] = ['order', 'direct', 'group']

/** Unread count as a small pill; «99+» past 99. */
export function UnreadPill({ count, muted, className }: { count: number; muted?: boolean; className?: string }) {
  if (count <= 0) return null
  return (
    <span
      className={cn(
        'num inline-flex h-4.5 min-w-4.5 shrink-0 items-center justify-center rounded-full px-1.5 text-[10.5px] font-semibold leading-none',
        muted ? 'bg-hover text-muted' : 'bg-accent text-accent-fg',
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}

export function ChatAvatar({ chat, size = 36 }: { chat: Pick<Chat, 'kind' | 'title'>; size?: number }) {
  if (chat.kind === 'direct') return <Avatar name={chat.title || '·'} size={size} />
  const Icon = chat.kind === 'order' ? Receipt : Users
  return (
    <span
      className={cn('flex shrink-0 items-center justify-center rounded-full', chat.kind === 'order' ? 'bg-info-soft text-info' : 'bg-accent-soft text-accent')}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <Icon className="size-4" />
    </span>
  )
}

/**
 * «Заказы / Личные / Группы» with a search and the profile's chats, newest
 * activity first. The first page reloads every 15 s while the tab is
 * visible and whenever a chat changes here (read, sent, members).
 */
export const ChatList = observer(function ChatList({
  kind,
  onKindChange,
  selectedId,
  onOpen,
  toolbar,
}: {
  kind: ChatKind
  onKindChange: (kind: ChatKind) => void
  selectedId?: string | null
  onOpen: (chat: Chat) => void
  /** Extra controls under the search («Новый чат», «Новая группа»). */
  toolbar?: ReactNode
}) {
  const t = useT()
  const { chat: chatStore, session } = useStores()
  const readAll = session.can('chat.read_all')
  const [search, setSearch] = useState('')
  const [scope, setScope] = useState<'mine' | 'all'>('mine')
  const all = readAll && scope === 'all'
  const [items, setItems] = useState<Chat[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const seq = useRef(0)
  const loaded = useRef(PAGE)

  const load = useCallback(
    async (offset: number, quiet = false) => {
      const id = ++seq.current
      // Polls and catch-ups refresh in place, without a spinner.
      if (!quiet) setLoading(true)
      try {
        // A refresh re-reads everything shown so far (at most 100), so a poll never shortens the list.
        const limit = offset === 0 ? Math.min(100, Math.max(PAGE, loaded.current)) : PAGE
        const page = await chatService.list({ kind, search: search || undefined, scope: all ? 'all' : undefined, limit, offset })
        if (id !== seq.current) return
        setItems((current) => (offset === 0 ? page.items : [...current, ...page.items.filter((row) => !current.some((item) => item.id === row.id))]))
        setCount(page.count)
        loaded.current = offset === 0 ? Math.max(PAGE, page.items.length) : loaded.current + page.items.length
        setError('')
      } catch (cause) {
        const apiError = toApiError(cause)
        if (id === seq.current && !apiError.isCancelled) setError(apiError.message)
      } finally {
        if (id === seq.current) setLoading(false)
      }
    },
    [kind, search, all],
  )

  // A new tab or search starts from the first page.
  useEffect(() => {
    loaded.current = PAGE
    void load(0)
  }, [load])

  // Something changed here (read, sent, a new chat): catch up now.
  const revision = chatStore.revision
  const firstRevision = useRef(revision)
  useEffect(() => {
    if (revision === firstRevision.current) return
    void load(0, true)
  }, [revision, load])

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(0, true)
    }, POLL_MS)
    return () => window.clearInterval(timer)
  }, [load])

  const unread = chatStore.unread
  const emptyText = search ? t('chat.empty.search') : t(`chat.empty.${kind}`)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-col gap-2.5 px-3 pb-2.5 pt-3">
        <Tabs
          label={t('chat.tabsLabel')}
          value={kind}
          onChange={onKindChange}
          className="w-full [&>button]:flex-1 [&>button]:justify-center"
          items={CHAT_KINDS.map((value) => ({
            value,
            label: t(`chat.tabs.${value}`),
            badge: unread[value] > 0 ? <UnreadPill count={unread[value]} /> : undefined,
          }))}
        />
        {readAll && (
          <Tabs
            label={t('chat.scope.label')}
            value={scope}
            onChange={setScope}
            variant="underline"
            items={[
              { value: 'mine', label: t('chat.scope.mine') },
              { value: 'all', label: t('chat.scope.all') },
            ]}
          />
        )}
        <SearchInput value={search} onChange={setSearch} placeholder={t('chat.search')} className="sm:w-full" />
        {toolbar}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-2" role="list">
        {error && items.length === 0 && (
          <div className="flex flex-col items-center gap-2 px-3 py-8 text-center">
            <p className="text-[length:calc(13px*var(--text-scale))] text-danger">
              {t('chat.listFailed')}: {error}
            </p>
            <Button variant="secondary" size="sm" onClick={() => void load(0)}>
              {t('chat.retry')}
            </Button>
          </div>
        )}
        {!error && !loading && items.length === 0 && (
          <p className="px-4 py-10 text-center text-[length:calc(13px*var(--text-scale))] text-muted">{emptyText}</p>
        )}
        {items.map((row) => {
          const selected = row.id === selectedId
          return (
            <button
              key={row.id}
              type="button"
              role="listitem"
              aria-current={selected || undefined}
              onClick={() => onOpen(row)}
              className={cn(
                'flex w-full items-center gap-3 rounded-[calc(10px*var(--radius-scale))] px-2.5 py-2 text-left transition-colors',
                selected ? 'bg-accent-soft' : 'hover:bg-hover',
              )}
            >
              <ChatAvatar chat={row} />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-2">
                  <span className={cn('min-w-0 flex-1 truncate text-[length:calc(13.5px*var(--text-scale))]', row.unread_count > 0 ? 'font-semibold text-fg' : 'font-medium text-fg')}>
                    {chatTitle(row, t)}
                  </span>
                  <span className="num shrink-0 text-[length:calc(11px*var(--text-scale))] text-subtle">{formatListTime(row.last_message_at || row.created_at)}</span>
                </span>
                <span className="mt-0.5 flex items-center gap-2">
                  <span className={cn('min-w-0 flex-1 truncate text-[length:calc(12.5px*var(--text-scale))]', row.unread_count > 0 ? 'text-fg-2' : 'text-muted')}>
                    {previewText(row.last_message, t) || ' '}
                  </span>
                  {row.muted && <BellOff className="size-3.5 shrink-0 text-subtle" aria-label={t('chat.muted')} />}
                  <UnreadPill count={row.unread_count} muted={row.muted} />
                </span>
              </span>
            </button>
          )
        })}
        {loading && (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        )}
        {!loading && items.length < count && (
          <div className="flex justify-center py-1.5">
            <Button variant="ghost" size="sm" onClick={() => void load(items.length)}>
              {t('chat.more')}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
})
